/**
 * @file FilterRangeInput atom — bordered numeric input for range filters.
 * Encapsulates two off-token style values that have no closed equivalents:
 * paddingVertical: 10 px (between the 2=8 and 3=12 scale steps) and the
 * web focus-outline reset. Colors follow semantic-token equivalents
 * (surface-base, outline-strong/emphasis, content-muted) resolved via the
 * isDark flag so callers carry no UNSAFE_style.
 * @module components/atoms/FilterRangeInput
 */

import React from "react";
import { Platform, StyleSheet, TextInput, View } from "react-native";

/** surface-base: light #FFFFFF / dark #121212 */
const BG_LIGHT = "#FFFFFF";
const BG_DARK = "#121212";
/** border-outline-strong (light) / border-outline-emphasis dark mode */
const BORDER_LIGHT = "#D3D3D3";
const BORDER_DARK = "#DDDCDB";
/**
 * The old `content-muted` light / `content-subtle` dark grey, #8C8C8C in both.
 * Since SE-8550 content-muted is #737373 in light, so this no longer matches
 * a token in light mode — tracked in the mode-ternary inventory (Q5).
 */
const TEXT_COLOR = "#8C8C8C";
const PLACEHOLDER_COLOR = "#9CA3AF";

/** Props for the FilterRangeInput atom. */
export interface FilterRangeInputProps {
  readonly value: string;
  readonly onChangeText: (text: string) => void;
  readonly onBlur?: () => void;
  readonly placeholder?: string;
  readonly testID?: string;
  /** Apply dark-mode palette when true. Defaults to false. */
  readonly isDark?: boolean;
}

/**
 * Numeric range-filter input with bordered container.
 * Replaces Box + TextInput + two UNSAFE_style props so call sites are
 * style-prop-free.
 * @param root0 - Component props
 * @param root0.value - Current text value
 * @param root0.onChangeText - Change handler
 * @param root0.onBlur - Blur handler
 * @param root0.placeholder - Placeholder text
 * @param root0.testID - Test identifier
 * @param root0.isDark - Dark-mode flag
 */
export function FilterRangeInput({
  value,
  onChangeText,
  onBlur,
  placeholder,
  testID,
  isDark = false,
}: FilterRangeInputProps) {
  return (
    <View
      style={[
        styles.container,
        {
          borderColor: isDark ? BORDER_DARK : BORDER_LIGHT,
          backgroundColor: isDark ? BG_DARK : BG_LIGHT,
        },
      ]}
    >
      <TextInput
        style={inputStyle}
        value={value}
        onChangeText={onChangeText}
        onBlur={onBlur}
        keyboardType="numeric"
        placeholder={placeholder}
        placeholderTextColor={PLACEHOLDER_COLOR}
        testID={testID}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    overflow: "hidden",
    borderRadius: 0,
    borderWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
});

const inputStyle =
  Platform.OS === "web"
    ? ({
        color: TEXT_COLOR,
        fontSize: 14,
        lineHeight: 21,
        outlineStyle: "none",
      } as object)
    : { color: TEXT_COLOR, fontSize: 14, lineHeight: 21 };

FilterRangeInput.displayName = "FilterRangeInput";
